import { FC, useMemo } from 'react';
import cn from 'classnames';
import { Link } from 'react-router-dom';
import { getEnding } from '@datassential/platform-ui-lib';
import { Tooltip } from 'react-tooltip';
import ReactDOM from 'react-dom';

import './SearchSource.styles.scss';
import { IconRead } from '../../../../icons';
import { IconDownload } from '../../../../icons';
import { iFile } from '../../../../core/models/File';
import Button from '../../../../components/Button/Button';
import RoutesEnum from '../../../../core/enums/RoutesEnum';
import FileTypesEnum from '../../../../core/enums/FileTypesEnum';
import { iPublication } from '../../../../core/models/Publication';
import DownloadList from '../../../../components/DownloadList/DownloadList';
import RequestAccessButton from '../../../../components/RequestAccessButton/RequestAccessButton';
import { BreakpointsEnum, useMediaQuery } from '../../../../core/hooks/useMediaQuery';

interface iSearchSourceProps {
    onRead: (pdfFile: iFile, title: string, initialPage?: number) => void;
    source: iPublication;
}

const SearchSource: FC<iSearchSourceProps> = ({ onRead, source }) => {
    const sourceUrl = RoutesEnum.DocumentDetails.replace(':id', source.id.toString());
    const tooltipId = `download-list-tooltip-${source.id}`;
    const portalRoot = document.getElementById('portal-root');
    const isXsMobile = useMediaQuery(BreakpointsEnum.XS);
    const isMobile = useMediaQuery(BreakpointsEnum.SM);

    const pdfFile = useMemo(() => {
        return source.files.find((file) => file.type === FileTypesEnum.Pdf);
    }, [source]);

    return (
        <div className="SearchSource">
            <div className="SearchSource__image">
                {source.thumbnail && (
                    <Link to={sourceUrl} target="_blank" rel="noopener noreferrer">
                        <img src={source.thumbnail} alt={source.title} />
                    </Link>
                )}
            </div>
            <div className="SearchSource__content">
                <section>
                    <div className="SearchSource__title">
                        <Link to={sourceUrl} className="SearchSource__title" target="_blank" rel="noopener noreferrer">
                            {source.title}
                        </Link>
                    </div>
                    {source.pages && source.pages.length > 0 && (
                        <div className="SearchSource__text">
                            {getEnding('page', source.pages.length)}&nbsp;
                            {source.pages.map((page, i, arr) => (
                                <>
                                    <span
                                        key={page}
                                        className={cn({ 'SearchSource__clickable-page': !!pdfFile })}
                                        onClick={() => {
                                            if (pdfFile) {
                                                onRead(pdfFile, source.title, page);
                                            }
                                        }}
                                    >
                                        {page}
                                    </span>
                                    {i < arr.length - 1 && ', '}
                                </>
                            ))}
                        </div>
                    )}
                    {source.createdDate && <div className="SearchSource__text">published {source.createdDate}</div>}
                </section>

                <div className="SearchSource__actions">
                    {pdfFile && !isMobile && (
                        <Button
                            className="SearchSource__button"
                            options={['secondary']}
                            onClick={() => onRead(pdfFile, source.title)}
                        >
                            <IconRead className={`${isXsMobile ? 'Icon24' : 'Icon32'}`} />
                            Read
                        </Button>
                    )}

                    {!source.hasAccess && (
                        <RequestAccessButton
                            articleId={source.id}
                            className="SearchSource__button"
                            documentType={source.publicationType}
                        />
                    )}

                    {source.files.length > 0 && source.hasAccess && (
                        <>
                            <div className="SearchSource__button-container" data-tooltip-id={tooltipId}>
                                <Button className="SearchSource__button" options={['primary']}>
                                    <IconDownload className={`${isXsMobile ? 'Icon24' : 'Icon32'}`} />
                                    Download
                                </Button>
                                {portalRoot &&
                                    ReactDOM.createPortal(
                                        <Tooltip
                                            id={tooltipId}
                                            place="bottom-start"
                                            className="Tooltip__dropdown"
                                            delayHide={700}
                                            clickable
                                        >
                                            <DownloadList items={source.files} reportId={source.id} />
                                        </Tooltip>,
                                        portalRoot
                                    )}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SearchSource;
